<script lang="ts" setup>
import { computed, ref, watch, type PropType } from 'vue';

import { DivTableColumn, divTableSortDirections } from './div-table-helpers';
import DivTableCell from './DivTableCell.vue';

const props = defineProps({
  stickyHeader: { type: Boolean, default: false },
  value: { type: Array as PropType<Array<DivTableColumn<unknown>>>, required: true },
});
const emit = defineEmits<{
  (e: 'input', value: Array<DivTableColumn<unknown>>): void;
}>();

const internalColumns = ref(props.value);
const columnsToShow = computed(() => internalColumns.value.filter((i) => !i.hidden));

watch(
  () => props.value,
  () => {
    internalColumns.value = props.value;
  },
);

const sort = (column: DivTableColumn<unknown>) => {
  if (!column.sortable) return; // Don't sort if the column is not sortable

  emit(
    'input',
    internalColumns.value.map((val) => {
      if (val !== column) {
        // eslint-disable-next-line no-param-reassign
        val.sorted = false;

        return val;
      }

      if (val.sorted === true) {
        // eslint-disable-next-line no-param-reassign
        val.sortDirection = val.sortDirection === divTableSortDirections.asc ? divTableSortDirections.desc : divTableSortDirections.asc;
      } else {
        // eslint-disable-next-line no-param-reassign
        val.sorted = true;
      }

      return val;
    }),
  );
};
</script>

<template>
  <div :class="['div-table-header', { 'div-table-header--sticky-header': stickyHeader }]">
    <div-table-cell
      v-for="(column, index) in columnsToShow"
      :key="index"
      :sortable="column.sortable && column.sortFunction"
      :class="['div-table-header__cell', column.columnClass, column.title.trim().length == 0 ? 'empty' : '']"
      @click="sort(column)"
    >
      <div
        v-if="column.title"
        :class="[`div-table-header__title`, { sorted: column.sorted }]"
      >
        {{ column.title }}
      </div>
      <ot-icon
        v-if="column.sortable"
        class="sort-icon"
        :inherit-stroke="true"
        :name="column.sorted ? 'full-arrow-up' : 'arrow-3'"
        :class="{ [column.sortDirection]: true, sorted: column.sorted }"
      />
    </div-table-cell>
  </div>
</template>

<style lang="scss">
.div-table-header {
  display: grid;
  align-items: stretch;
  background-color: rgb(var(--v-theme-elevation-1));
  color: rgb(var(--v-theme-text-tertiary));
  grid-template-columns: var(--grid-table-columns);

  &__title {
    display: inline;
  }

  &__cell {
    padding: 1px 2px;
    background-color: rgb(var(--v-theme-elevation-1));
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-regular);
    white-space: nowrap;

    .xs & {
      font-size: var(--font-size-md);
    }

    &__title {
      display: inline-block;
    }

    .sort-icon {
      display: none;
      width: 10px;
      opacity: 0;

      &.sorted.sorted {
        display: inline-block;
        opacity: 1;
      }

      &.desc {
        transform: rotateX(180deg);
      }
    }

    &.empty {
      width: 0;
      padding: 0;
      border: 0;
    }

    &:hover .sort-icon {
      opacity: 0.5;
    }

    &:not(.div-table-cell--sortable):hover .sort-icon {
      opacity: 0;
    }

    &:first-of-type {
      padding: 0 10px;
    }

    &:last-of-type {
      padding-right: 10px;
    }
  }
}

.div-table-header--sticky-header {
  position: sticky;
  z-index: var(--table-sticky-header-z-index);
  top: 0;
}
</style>
